import React from 'react';
import PropTypes from 'prop-types';
import { UX2, constants } from '@wsb/guac-widget-core';

const SlideshowHero = ({
  slide,
  index,
  forceH1,
  alignmentOption,
  heroIdPrefix,
  themeConfig,
  renderMode,
  currentSlide,
  showText,
  slideWidthPercent,
  fixedPosition,
  style = {},
  phone
}) => {
  const {
    maxWidthPercent = 100,
    heroTransition = 'fade',
    heroClass: Hero,
    heroProps = {},
    heroJustifyContent = 'center'
  } = themeConfig;
  const isCurrentSlide = currentSlide === index;
  const shouldShow = isCurrentSlide && showText;

  const visibilityStyle =
    heroTransition === 'fade'
      ? {
        opacity: shouldShow ? 1 : 0,
        transition: 'opacity 1s'
      }
      : {
        opacity: shouldShow ? 1 : 0,
        transition: 'opacity 0.3s'
      };

  const useFixedPosition = fixedPosition && heroTransition !== 'slide';

  const alignmentToAlignItems = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end'
  };

  const baseContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: heroJustifyContent,
    alignItems: alignmentToAlignItems[alignmentOption] || 'center',
    width: '100%',
    pointerEvents: 'none',
    ...(heroJustifyContent === 'flex-end' && { paddingBottom: 'xxxlarge' }),
    ...style,
    ...(useFixedPosition && {
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 1000
    }),
    ...(!useFixedPosition && {
      position: 'relative',
      zIndex: 10,
      height: '100%'
    }),
    ...visibilityStyle
  };

  const styles = {
    container: baseContainerStyle,
    innerContainer: {
      pointerEvents: 'auto',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingHorizontal: 'xlarge',
      width: `${slideWidthPercent || maxWidthPercent}%`,
      ['@md']: {
        paddingHorizontal: 'large'
      }
    }
  };

  const { tagline, tagline2, cta, ctaList } = slide;
  const hasCta = cta?.enabled;
  const hasCtaList = ctaList?.some(item => item?.enabled);
  const hasContent = tagline || tagline2 || hasCta || hasCtaList;

  const shouldRenderContainer =
    renderMode === constants.renderModes.PUBLISH ? hasContent : Hero && hasContent;

  return shouldRenderContainer ? (
    <UX2.Element.Block
      style={ styles.container }
      data-slide-hero={ `${heroIdPrefix}-${index}` }
      data-slide-visible={ shouldShow ? 'true' : 'false' }
      data-hero-transition={ heroTransition }
    >
      { renderMode !== constants.renderModes.PUBLISH && Hero && (
        <UX2.Element.Container style={ styles.innerContainer }>
          <Hero
            forceH1={ forceH1 }
            slideIndex={ index }
            tagline={ tagline }
            tagline2={ tagline2 }
            cta={ hasCta && cta }
            ctaList={ hasCtaList && ctaList }
            alignmentOption={ alignmentOption }
            phone={ phone }
            { ...heroProps }
            isSlideshow
            hasCTAEnabled={ hasCta || hasCtaList }
          />
        </UX2.Element.Container>
      ) }
    </UX2.Element.Block>
  ) : null;
};

SlideshowHero.propTypes = {
  slide: PropTypes.object,
  index: PropTypes.number,
  forceH1: PropTypes.bool,
  alignmentOption: PropTypes.string,
  heroIdPrefix: PropTypes.string,
  themeConfig: PropTypes.object,
  renderMode: PropTypes.string,
  currentSlide: PropTypes.number,
  showText: PropTypes.bool,
  slideWidthPercent: PropTypes.number,
  style: PropTypes.object,
  fixedPosition: PropTypes.bool,
  phone: PropTypes.element
};

SlideshowHero.defaultProps = {
  fixedPosition: true
};

export default SlideshowHero;
